<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n, I18nT } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Icon from 'next/icon/Icon.vue';
import NextButton from 'next/button/Button.vue';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import globalConstants from 'dashboard/constants/globals.js';
import {
  setupFacebookSdk,
  initWhatsAppEmbeddedSignup,
  createMessageHandler,
  isValidBusinessData,
} from './whatsapp/utils';
import SalvyApi from 'dashboard/api/salvy';
import ActionCable from '@rails/actioncable';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

// State
const fbSdkLoaded = ref(false);
const isProcessing = ref(false);
const processingMessage = ref('');
const authCodeReceived = ref(false);
const authCode = ref(null);
const businessData = ref(null);
const isAuthenticating = ref(false);

// Salvy State
const showSalvyForm = ref(false);
const salvyAreaCodes = ref([]);
const selectedAreaCode = ref('');
const isProvisioningSalvy = ref(false);
const waitingForSms = ref(false);
const salvyPhoneNumber = ref('');
const salvyCable = ref(null);
const isFrictionlessFlow = ref(false);

const benefits = computed(() => [
  {
    key: 'EASY_SETUP',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.EASY_SETUP'),
  },
  {
    key: 'SECURE_AUTH',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.SECURE_AUTH'),
  },
  {
    key: 'AUTO_CONFIG',
    text: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.BENEFITS.AUTO_CONFIG'),
  },
]);

const showLoader = computed(() => isAuthenticating.value || isProcessing.value);

// Error handling
const handleSignupError = data => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;

  const errorMessage =
    data.error ||
    data.message ||
    t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
  useAlert(errorMessage);
};

const handleSignupCancellation = () => {
  isProcessing.value = false;
  authCodeReceived.value = false;
  isAuthenticating.value = false;
};

const handleSignupSuccess = inboxData => {
  isProcessing.value = false;
  isAuthenticating.value = false;

  if (inboxData && inboxData.id) {
    useAlert(t('INBOX_MGMT.FINISH.MESSAGE'));
    router.replace({
      name: 'settings_inboxes_add_agents',
      params: {
        page: 'new',
        inbox_id: inboxData.id,
      },
    });
  } else {
    useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUCCESS_FALLBACK'));
    router.replace({
      name: 'settings_inbox_list',
    });
  }
};

// Signup flow
const completeSignupFlow = async businessDataParam => {
  if (!authCodeReceived.value || !authCode.value) {
    handleSignupError({
      error: t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_NOT_COMPLETED'),
    });
    return;
  }

  isProcessing.value = true;
  processingMessage.value = t(
    'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.PROCESSING'
  );

  try {
    const params = {
      code: authCode.value,
      business_id: businessDataParam.business_id,
      waba_id: businessDataParam.waba_id,
      phone_number_id: businessDataParam?.phone_number_id || '',
      is_business_app_onboarding:
        businessDataParam?.is_business_app_onboarding || false,
    };

    const responseData = await store.dispatch(
      'inboxes/createWhatsAppEmbeddedSignup',
      params
    );

    authCode.value = null;
    handleSignupSuccess(responseData);
  } catch (error) {
    const errorMessage =
      parseAPIErrorResponse(error) ||
      t('INBOX_MGMT.ADD.WHATSAPP.API.ERROR_MESSAGE');
    handleSignupError({ error: errorMessage });
  }
};

// Message handling
const handleEmbeddedSignupData = async data => {
  if (data.event === 'FINISH_ONLY_WABA') {
    if (
      authCodeReceived.value &&
      authCode.value &&
      data.data &&
      data.data.waba_id
    ) {
      try {
        processingMessage.value =
          'Registering your Salvy number in the background...';
        await SalvyApi.registerNumber({
          waba_id: data.data.waba_id,
          phone_number: salvyPhoneNumber.value,
          cc: selectedAreaCode.value, // We need to ensure we pass country code or adjust based on area code
          display_name: 'AirysChat', // Hardcoded for MVP or extracted from input if available
        });
        useAlert(
          'Number successfully submitted to Meta. Waiting for SMS verification code...'
        );
      } catch (error) {
        handleSignupError({
          error: 'Failed to initiate background number registration.',
        });
      } finally {
        isAuthenticating.value = false;
      }
    } else {
      handleSignupError({
        error: 'Missing Auth Code or WABA ID for background registration.',
      });
    }
  } else if (
    data.event === 'FINISH' ||
    data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING'
  ) {
    const businessDataLocal = data.data;

    if (isValidBusinessData(businessDataLocal)) {
      businessData.value = {
        ...businessDataLocal,
        is_business_app_onboarding:
          data.event === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING',
      };
      if (authCodeReceived.value && authCode.value) {
        await completeSignupFlow(businessData.value);
      } else {
        processingMessage.value = t(
          'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_AUTH'
        );
      }
    } else {
      handleSignupError({
        error: t(
          'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.INVALID_BUSINESS_DATA'
        ),
      });
    }
  } else if (data.event === 'CANCEL') {
    handleSignupCancellation();
  } else if (data.event === 'error') {
    handleSignupError({
      error:
        data.error_message ||
        t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SIGNUP_ERROR'),
      error_id: data.error_id,
      session_id: data.session_id,
    });
  }
};

const handleSignupMessage = createMessageHandler(handleEmbeddedSignupData);

const launchEmbeddedSignup = async () => {
  try {
    isAuthenticating.value = true;
    processingMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.AUTH_PROCESSING'
    );

    await setupFacebookSdk(
      window.chatwootConfig?.whatsappAppId,
      window.chatwootConfig?.whatsappApiVersion
    );
    fbSdkLoaded.value = true;

    const featureType = isFrictionlessFlow.value
      ? 'only_waba_sharing'
      : 'whatsapp_business_app_onboarding';

    const configId =
      isFrictionlessFlow.value &&
      window.globalConfig?.WHATSAPP_BYPASS_CONFIGURATION_ID
        ? window.globalConfig.WHATSAPP_BYPASS_CONFIGURATION_ID
        : window.chatwootConfig?.whatsappConfigurationId;

    const code = await initWhatsAppEmbeddedSignup(configId, featureType);

    authCode.value = code;
    authCodeReceived.value = true;
    processingMessage.value = t(
      'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.WAITING_FOR_BUSINESS_INFO'
    );

    if (businessData.value) {
      completeSignupFlow(businessData.value);
    }
  } catch (error) {
    if (error.message === 'Login cancelled') {
      isProcessing.value = false;
      isAuthenticating.value = false;
      useAlert(t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.CANCELLED'));
    } else {
      handleSignupError({
        error:
          error.message ||
          t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SDK_LOAD_ERROR'),
      });
    }
  }
};

// Salvy Flow
const setupActionCable = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const cableUrl = `${protocol}//${window.location.host}/cable`;
  salvyCable.value = ActionCable.createConsumer(cableUrl);
  salvyCable.value.subscriptions.create('SalvySmsChannel', {
    received: data => {
      if (data.to && salvyPhoneNumber.value.includes(data.to)) {
        useAlert(`Salvy SMS Received: ${data.body}`);
        waitingForSms.value = false;
      }
    },
  });
};

const toggleSalvyForm = async () => {
  showSalvyForm.value = !showSalvyForm.value;
  if (showSalvyForm.value && salvyAreaCodes.value.length === 0) {
    try {
      const response = await SalvyApi.getAreaCodes();
      if (response.data && response.data.areaCodes) {
        salvyAreaCodes.value = response.data.areaCodes
          .filter(c => c.available)
          .map(c => c.areaCode);
      } else {
        salvyAreaCodes.value = [];
      }
    } catch (error) {
      useAlert('Failed to load area codes from Salvy.');
    }
  }
};

const provisionSalvyNumber = async () => {
  if (!selectedAreaCode.value) return;
  isProvisioningSalvy.value = true;
  try {
    const response = await SalvyApi.createVirtualNumber(selectedAreaCode.value);
    const newNumber =
      response.data.phoneNumber || response.data.data.phoneNumber;
    salvyPhoneNumber.value = newNumber.toString().startsWith('+')
      ? newNumber
      : `+${newNumber}`;
    showSalvyForm.value = false;
    isFrictionlessFlow.value = true;
    waitingForSms.value = true;
    useAlert(
      'Number provisioned successfully! Waiting for SMS verification code...'
    );
  } catch (error) {
    useAlert(error.response?.data?.error || 'Failed to provision number.');
  } finally {
    isProvisioningSalvy.value = false;
  }
};

// Lifecycle
const setupMessageListener = () => {
  window.addEventListener('message', handleSignupMessage);
};

const cleanupMessageListener = () => {
  window.removeEventListener('message', handleSignupMessage);
};

const initialize = () => {
  setupMessageListener();
  setupActionCable();
};

onMounted(() => {
  initialize();
});

onBeforeUnmount(() => {
  cleanupMessageListener();
  if (salvyCable.value) {
    salvyCable.value.disconnect();
  }
});
</script>

<template>
  <div class="h-full">
    <!-- Salvy State Displays (Always visible if active) -->
    <div
      v-if="salvyPhoneNumber"
      class="p-3 mb-4 text-sm text-n-green-text bg-n-green-background rounded-md border border-n-green-border font-mono text-center"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.YOUR_SALVY_NUMBER') }}
      <strong>{{ salvyPhoneNumber }}</strong>
    </div>

    <div
      v-if="waitingForSms"
      class="p-3 mb-4 text-sm text-n-blue-text bg-n-blue-background rounded-md border border-n-blue-border"
    >
      {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.WAITING_FOR_SMS') }}
    </div>

    <LoadingState v-if="showLoader" :message="processingMessage" />

    <div v-else>
      <div class="flex flex-col items-start mb-6 text-start">
        <div class="flex justify-start mb-6">
          <div
            class="flex size-11 items-center justify-center rounded-full bg-n-alpha-2"
          >
            <Icon icon="i-woot-whatsapp" class="text-n-slate-10 size-6" />
          </div>
        </div>

        <h3 class="mb-2 text-base font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.TITLE') }}
        </h3>
        <p class="text-sm leading-[24px] text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.DESC') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <div
          v-for="benefit in benefits"
          :key="benefit.key"
          class="flex gap-2 items-center text-sm text-n-slate-11"
        >
          <Icon icon="i-lucide-check" class="text-n-slate-11 size-4" />
          {{ benefit.text }}
        </div>
      </div>

      <div class="flex flex-col gap-2 mb-6">
        <I18nT
          keypath="INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.TEXT"
          tag="span"
          class="text-sm text-n-slate-11"
        >
          <template #link>
            <a
              :href="globalConstants.WHATSAPP_EMBEDDED_SIGNUP_DOCS_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="underline text-n-brand"
            >
              {{
                $t(
                  'INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.LEARN_MORE.LINK_TEXT'
                )
              }}
            </a>
          </template>
        </I18nT>
      </div>

      <!-- Salvy Form Injection -->
      <div class="flex flex-col gap-2 mb-6 border-t border-n-weak pt-6 mt-4">
        <div class="flex justify-between items-center">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.EMBEDDED_LINK_TEXT') }}
          </h4>
          <a
            href="#"
            class="text-sm text-n-brand underline"
            @click.prevent="toggleSalvyForm"
          >
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.LINK_TEXT') }}
          </a>
        </div>

        <div
          v-if="showSalvyForm"
          class="p-4 mt-2 mb-4 bg-n-alpha-1 rounded-md border border-n-weak"
        >
          <h4 class="text-sm font-semibold text-n-slate-12 mb-2">
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.FORM_TITLE') }}
          </h4>
          <p class="text-xs text-n-slate-11 mb-4">
            <strong>{{
              $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_LABEL')
            }}</strong>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_DESC') }}
            <strong>{{
              $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_PRICE')
            }}</strong>
            {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BILLING_SUFFIX') }}
          </p>
          <div class="flex gap-2">
            <select
              v-model="selectedAreaCode"
              class="block w-full text-sm border border-n-weak rounded-md p-2"
            >
              <option value="" disabled>
                {{ $t('INBOX_MGMT.ADD.WHATSAPP.SALVY.AREA_CODE_PLACEHOLDER') }}
              </option>
              <option v-for="ddd in salvyAreaCodes" :key="ddd" :value="ddd">
                {{ ddd }}
              </option>
            </select>
            <NextButton
              :is-loading="isProvisioningSalvy"
              :disabled="!selectedAreaCode"
              solid
              blue
              :label="$t('INBOX_MGMT.ADD.WHATSAPP.SALVY.BUY_BUTTON')"
              @click.prevent="provisionSalvyNumber"
            />
          </div>
        </div>
      </div>

      <div class="flex mt-4">
        <NextButton
          :disabled="isAuthenticating"
          :is-loading="isAuthenticating"
          faded
          slate
          class="w-full"
          @click="launchEmbeddedSignup"
        >
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EMBEDDED_SIGNUP.SUBMIT_BUTTON') }}
        </NextButton>
      </div>
    </div>
  </div>
</template>
